import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Input } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import BackButton from "../../../../components/BackButton";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { getWithdrawSteps } from "../../constants/steps";
import {
  getManualProfile,
  ManualProfileRequest,
  upsertManualProfile,
  updateWithdrawStatus,
} from "../../../../services/withdrawApiService";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { detectBankByClabe } from "../../../../constants/banks";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep4Manual";

const WithdrawPageStep4Manual: React.FC = () => {
  const { t, i18n } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [detectedBank, setDetectedBank] = useState<string>("");

  const [form, setForm] = useState<ManualProfileRequest>({
    withdrawID: "",
    country: "MX",
    accountType: "cheques",
    contactEmail: user?.email || "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [alert, setAlert] = useState<{
    message: string;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const storedWithdrawal = localStorage.getItem("withdrawal");
  const withdrawID = storedWithdrawal
    ? (JSON.parse(storedWithdrawal).withdrawalID as string | undefined)
    : undefined;

  useEffect(() => {
    const load = async () => {
      if (!withdrawID) {
        setAlert({
          message: t("missingWithdrawId", "No se encontró el retiro activo."),
          level: "error",
        });
        setLoading(false);
        return;
      }
      try {
        const existing = await getManualProfile(withdrawID);
        if (existing) {
          setForm({
            withdrawID,
            bank: existing.bank,
            account: existing.account,
            clabe: existing.clabe,
            accountType: existing.accountType,
            holderName: existing.holderName,
            contactPhone: existing.contactPhone,
            dateOfBirth: existing.dateOfBirth,
            rfc: existing.rfc,
            addressLine1: existing.addressLine1,
            addressLine2: existing.addressLine2,
            city: existing.city,
            state: existing.state,
            postalCode: existing.postalCode,
            country: existing.country || "MX",
            contactEmail: user?.email || existing.contactEmail || "",
          });
        } else {
          setForm((prev) => ({
            ...prev,
            withdrawID,
            country: "MX",
            accountType: "cheques",
            contactEmail: user?.email || "",
          }));
        }
      } catch (err) {
        console.error("Error loading manual profile", err);
        setAlert({
          message: t(
            "loadError",
            "No pudimos cargar tus datos. Intenta más tarde."
          ),
          level: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [withdrawID, t]);

  const handleChange = (field: keyof ManualProfileRequest, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateRequired = (): boolean => {
    const requiredFields: (keyof ManualProfileRequest)[] = [
      "bank",
      "clabe",
      "accountType",
      "holderName",
      "contactPhone",
      "dateOfBirth",
      "rfc",
      "addressLine1",
      "city",
      "state",
      "postalCode",
      "country",
    ];
    const missing = requiredFields.filter((f) => !form[f]);
    if (missing.length) {
      setAlert({
        message: t("missingFields", "Completa todos los campos obligatorios."),
        level: "warn",
      });
      return false;
    }
    const clean = (form.clabe || "").replace(/\s+/g, "").toUpperCase();
    const isClabe = /^\d{18}$/.test(clean);
    const isSwift = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(clean);
    const isIban = /^[A-Z]{2}[A-Z0-9]{13,32}$/.test(clean);
    if (!isClabe && !isSwift && !isIban) {
      setAlert({
        message: t(
          "invalidAccountId",
          "Introduce una CLABE de 18 dígitos o un IBAN/SWIFT válido."
        ),
        level: "warn",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!withdrawID) return;
    if (!validateRequired()) return;
    setSubmitting(true);
    setAlert(null);
    try {
      await upsertManualProfile({
        ...form,
        withdrawID,
        contactName: form.holderName,
        contactEmail: user?.email || form.contactEmail,
        bank: detectedBank || form.bank,
      });
      // Set status to pending_confirmation while user completes manual flow.
      if (withdrawID) {
        await updateWithdrawStatus({
          withdrawID,
          status: "pending_confirmation",
        });
      }
      navigate("/my-table/withdraw/step5-manual");
    } catch (err) {
      console.error("Error saving manual profile", err);
      setAlert({
        message: t(
          "saveError",
          "No pudimos guardar tus datos. Intenta de nuevo."
        ),
        level: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate("/my-table/withdraw/step3-manual");

  // Postal code geocoding to suggest city/state
  const [zipOptions, setZipOptions] = useState<
    { locality: string; city: string; state: string }[]
  >([]);
  const [zipSearching, setZipSearching] = useState(false);
  const [accountIdType, setAccountIdType] = useState<
    "clabe" | "swift" | "iban" | "unknown"
  >("unknown");
  const [zipError, setZipError] = useState<string | null>(null);

  const fetchZipData = useMemo(
    () => async (postal: string) => {
      if (!postal || postal.length < 5) return;
      setZipSearching(true);
      setZipError(null);
      try {
        const resp = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            postal + ", Mexico"
          )}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
        );
        const data = await resp.json();
        const opts: { locality: string; city: string; state: string }[] = [];
        if (data.results) {
          data.results.forEach((r: any) => {
            const comps: any[] = r.address_components || [];
            const localityComp = comps.find((c) => c.types.includes("sublocality"));
            const cityComp =
              comps.find((c) => c.types.includes("locality")) ||
              comps.find((c) => c.types.includes("postal_town"));
            const stateComp = comps.find((c) =>
              c.types.includes("administrative_area_level_1")
            );
            const localityName = localityComp?.short_name || "";
            const cityName = cityComp?.short_name || "";
            const stateName = stateComp?.short_name || "";

            if (r.postcode_localities && Array.isArray(r.postcode_localities)) {
              r.postcode_localities.forEach((loc: string) => {
                opts.push({ locality: loc, city: cityName, state: stateName });
              });
            } else {
              opts.push({
                locality: localityName,
                city: cityName,
                state: stateName,
              });
            }
          });
        }
        setZipOptions(opts);
        if (opts.length === 1) {
          setForm((prev) => ({
            ...prev,
            city: opts[0].locality || opts[0].city,
            state: opts[0].state,
          }));
        }
        if (opts.length === 0) {
          setZipError(
            t(
              "zipNotFound",
              "No encontramos ciudad/estado para este código postal."
            )
          );
        }
      } catch (err) {
        console.error("Error fetching zip geocode", err);
        setZipOptions([]);
        setZipError(
          t(
            "zipNotFound",
            "No encontramos ciudad/estado para este código postal."
          )
        );
      } finally {
        setZipSearching(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (form.postalCode && form.postalCode.length >= 5) {
      setForm((prev) => ({ ...prev, city: "", state: "" }));
      fetchZipData(form.postalCode);
    } else {
      setZipOptions([]);
      setForm((prev) => ({ ...prev, city: "", state: "" }));
    }
  }, [form.postalCode, fetchZipData]);

  // Detect bank from CLABE prefix.
  useEffect(() => {
    const clean = (form.clabe || "").replace(/\s+/g, "").toUpperCase();
    let detected: "clabe" | "swift" | "iban" | "unknown" = "unknown";
    const isSpanish = (t("lang", "es") as string).startsWith("es");

    const tryClabe = () => {
      if (/^\d{3,18}$/.test(clean)) {
        const bankName = clean.length >= 3 ? detectBankByClabe(clean) : null;
        if (bankName) {
          setDetectedBank(bankName);
          setForm((prev) => ({ ...prev, bank: bankName }));
        } else {
          setDetectedBank("");
        }
        if (clean.length === 18) {
          detected = "clabe";
        }
        return true;
      }
      return false;
    };

    const trySwift = () => {
      if (/^[A-Z0-9]{8}([A-Z0-9]{0,3})?$/.test(clean)) {
        detected = "swift";
        setDetectedBank("");
        return true;
      }
      return false;
    };

    const tryIban = () => {
      if (/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(clean)) {
        detected = "iban";
        setDetectedBank("");
        return true;
      }
      return false;
    };

    if (isSpanish) {
      tryClabe() || trySwift() || tryIban();
      if (detected === "unknown" && clean.length === 18) {
        detected = "clabe";
      }
    } else {
      trySwift() || tryIban() || tryClabe();
    }

    setAccountIdType(detected);
  }, [form.clabe, i18n.language, setForm]);

  return (
    <PageContainer>
      <div className="mt-16" />
      <StepIndicator steps={steps} currentStep={3} progressPercentage={80} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButton", "Regresar")} onClick={handleBack} />
        <h2 className="text-2xl font-bold mb-4 text-primary">
          {t("formHeading", "Ingresa tus datos para procesar el retiro")}
        </h2>
        {alert && <Alert message={alert.message} level={alert.level} />}
        {loading ? (
          <div className="text-gray-600">{t("loading", "Cargando...")}</div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-3/5 min-w-[220px]">
                  <Input
                    placeholder={t("clabePlaceholder", "18 dígitos")}
                    value={form.clabe || ""}
                    onChange={(e) =>
                      handleChange(
                        "clabe",
                        e.target.value.replace(/\s+/g, "").toUpperCase()
                      )
                    }
                    maxLength={34}
                  />
                </div>
                <div className="flex-1 min-h-[40px] flex items-center">
                  <span
                    className={`text-sm font-semibold text-primary transition-opacity duration-300 ${
                      detectedBank || accountIdType !== "unknown"
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  >
                    {detectedBank ||
                      (accountIdType === "swift" &&
                        t("bankSwift", "SWIFT/BIC")) ||
                      (accountIdType === "iban" &&
                        t("bankIban", "IBAN detectado")) ||
                      ""}
                  </span>
                </div>
              </div>
              {accountIdType !== "unknown" && (
                <div className="text-sm text-primary transition-opacity duration-300">
                  {accountIdType === "clabe"
                    ? t("typeClabe", "CLABE detectada")
                    : accountIdType === "swift"
                      ? t("bankSwift", "SWIFT/BIC")
                      : accountIdType === "iban"
                        ? t("bankIban", "IBAN detectado")
                        : ""}
                </div>
              )}
              <div>
                <label className="text-sm text-gray-700 mb-1 block">
                  {t("accountType", "Tipo de cuenta")}
                </label>
                <div className="flex gap-2">
                  {[
                    { key: "cheques", label: t("checking", "Cheques") },
                    { key: "ahorros", label: t("savings", "Ahorros") },
                    { key: "nomina", label: t("payroll", "Nómina") },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleChange("accountType", key)}
                      className={`inline-flex h-10 items-center justify-center rounded-full px-4 ${
                        form.accountType === key
                          ? "bg-primary text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Input
                placeholder={t("holderPlaceholder", "Nombre completo")}
                value={form.holderName || ""}
                onChange={(e) => handleChange("holderName", e.target.value)}
              />
              <Input
                placeholder={t("phonePlaceholder", "Teléfono")}
                value={form.contactPhone || ""}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
              />
              <Input
                type="date"
                value={form.dateOfBirth || ""}
                onChange={(e) => handleChange("dateOfBirth", e.target.value)}
              />
              <Input
                placeholder={t("rfcPlaceholder", "Ej. ABCD800101XXX")}
                value={form.rfc || ""}
                onChange={(e) => handleChange("rfc", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-700">
                  {t("country", "País")}
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.country || (i18n.language.startsWith("es") ? "MX" : "US")}
                  onChange={(e) => handleChange("country", e.target.value)}
                >
                  <option value="MX">{t("countryMX", "México")}</option>
                  <option value="US">{t("countryUS", "Estados Unidos")}</option>
                  <option value="CA">{t("countryCA", "Canadá")}</option>
                </select>
              </div>

              <div className="flex gap-3">
                <Input
                  className="flex-1"
                  placeholder={t("addressPlaceholder", "Calle y número")}
                  value={form.addressLine1 || ""}
                  onChange={(e) => handleChange("addressLine1", e.target.value)}
                />
                <Input
                  className="w-1/3 min-w-[120px]"
                  placeholder={t("address2Placeholder", "Num. interior (opcional)")}
                  value={form.addressLine2 || ""}
                  onChange={(e) => handleChange("addressLine2", e.target.value)}
                />
              </div>

              <Input
                placeholder={t("postalPlaceholder", "Código postal")}
                value={form.postalCode || ""}
                onChange={(e) => handleChange("postalCode", e.target.value)}
              />
              {zipSearching && (
                <div className="text-xs text-gray-500">
                  {t("searchingZip", "Buscando ciudad y estado...")}
                </div>
              )}
              {zipError && (
                <div className="text-xs text-red-500">{zipError}</div>
              )}
              {form.postalCode && form.postalCode.length >= 5 && zipOptions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {zipOptions.map((opt, idx) => (
                    <button
                      key={`${opt.city}-${opt.state}-${idx}`}
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          city: opt.locality || opt.city,
                          state: opt.state,
                        }))
                      }
                      className={`px-3 py-1 rounded-full text-sm ${
                        (form.city === (opt.locality || opt.city)) && form.state === opt.state
                          ? "bg-primary text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {opt.locality ? `${opt.locality}, ` : ""}
                      {opt.city && !opt.locality ? `${opt.city}, ` : ""}
                      {opt.state}
                    </button>
                  ))}
                </div>
              )}
              {form.postalCode && form.postalCode.length >= 5 && (form.city || form.state) && (
                <p className="text-sm text-gray-700">
                  {t("resolvedLocation", "Ubicación detectada")}:{" "}
                  {form.city ? `${form.city}, ` : ""}
                  {form.state}
                </p>
              )}
            </div>

            <Button
              label={
                submitting
                  ? t("saving", "Guardando...")
                  : t("saveAndContinue", "Guardar y continuar")
              }
              onClick={handleSubmit}
              type="primary"
              className="w-full mt-2"
              disabled={submitting}
            />
          </div>
        )}
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep4Manual));
